import isEqual from 'lodash-es/isEqual';
import type {
    default as LocationPickerState,
    SearchContext,
} from '../store/schema/LocationPickerState';
import findLocation from './findLocation';
import type LocationCommonWellItemViewState from '../store/schema/LocationCommonWellItemViewState';
import type SearchMode from '../store/schema/SearchMode';
import RequestStatus from '../store/schema/RequestStatus';
import getAdjustedEventTime from './getAdjustedEventTime';
import { now, differenceInMinutes } from 'owa-datetime';
import type { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';
import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';

export default function getLocationSuggestions(
    queryString: string,
    getPickerState: () => LocationPickerState,
    searchContext: SearchContext,
    onPickerStateChanged: (state: Partial<LocationPickerState>) => void
): Promise<LocationCommonWellItemViewState[]> {
    const { event, searchMode, currentDevicePosition } = searchContext;
    const requiredAttendees = event
        ? event.RequiredAttendees.map(attendee => attendee.Mailbox.EmailAddress)
        : [];
    const optionalAttendees = event
        ? event.OptionalAttendees.map(attendee => attendee.Mailbox.EmailAddress)
        : [];

    const timeSlot = event
        ? getAdjustedEventTime(event.Start, event.End, event.IsAllDayEvent)
        : null;

    const recurrence = event ? event.Recurrence : null;

    // event.ParentFolderId can be null for scenarios outside of scheduling assistant that may not set the
    // ParentFolderId on the event, that currently don't support connected accounts, such as Mail or TxP.
    const smtpAddressOfAccount = event?.ParentFolderId
        ? event.ParentFolderId.mailboxInfo.userIdentity
        : null;

    // if the query parameters are the same as the one in the state then return the existing suggestions
    if (
        alreadyRequested(
            smtpAddressOfAccount,
            queryString,
            searchMode,
            requiredAttendees,
            optionalAttendees,
            timeSlot,
            recurrence,
            getPickerState()
        )
    ) {
        // if the request is still pending, return the pending promise so that the suggestions
        // are refreshed when the request returns; otherwise return the current results;
        // if the suggestionSet came from the store then it is an observable array we need
        // to convert it to an array for the Fabric picker to pick up the results.
        if (getPickerState().findLocationRequestStatus === RequestStatus.Pending) {
            return getPickerState().pendingPromise;
        } else {
            return Promise.resolve(getPickerState().suggestionSet.slice());
        }
    }

    const curFindLocationRequestTimestamp = now();

    const request = findLocation(
        smtpAddressOfAccount,
        queryString,
        requiredAttendees,
        optionalAttendees,
        timeSlot,
        currentDevicePosition,
        searchMode,
        recurrence
    ).then(locationResults => {
        if (curFindLocationRequestTimestamp < getPickerState().displayedSuggestionsTimestamp) {
            // Skip processing responses which are older than the displayed suggestions.
            // This is used to prevent the old request's response coming back later than the newer one's, which could mess up the suggestion set.
            // Also we don't check queryString here to improve the client responsiveness, otherwise it is highly likely we never show any suggestions until the user stops typing.
            return getPickerState().suggestionSet;
        }

        const locationSuggestions = locationResults.map(location => {
            return {
                persona: location,
                text: location.displayName,
                isValid: true,
                isSelected: false,
                isContextMenuOpen: false,
            } as LocationCommonWellItemViewState;
        });

        // only update the picker state if the request is current
        if (getPickerState().pendingPromise === request) {
            onPickerStateChanged({
                smtpAddressOfAccount: smtpAddressOfAccount,
                findLocationRequestStatus: RequestStatus.Complete,
                suggestionSet: locationSuggestions,
                displayedSuggestionsTimestamp: curFindLocationRequestTimestamp,
                requestedSuggestionsRequiredAttendees: requiredAttendees,
                requestedSuggestionsOptionalAttendees: optionalAttendees,
                requestedSuggestionsSearchMode: searchMode,
                requestedSuggestionsTimeSlot: timeSlot,
                requestedSuggestionsRecurrence: recurrence,
                pendingPromise: null,
            });
        }

        return locationSuggestions;
    });

    onPickerStateChanged({
        queryString: queryString.trim(),
        smtpAddressOfAccount: smtpAddressOfAccount,
        findLocationRequestStatus: RequestStatus.Pending,
        requestedSuggestionsSearchMode: searchMode,
        requestedSuggestionsRequiredAttendees: requiredAttendees,
        requestedSuggestionsOptionalAttendees: optionalAttendees,
        requestedSuggestionsTimeSlot: timeSlot,
        requestedSuggestionsRecurrence: recurrence,
        pendingPromise: request,
    });

    return request;
}

function alreadyRequested(
    smtpAddressOfAccount: string,
    queryString: string,
    searchMode: SearchMode,
    requiredAttendees: string[],
    optionalAttendees: string[],
    timeSlot: OwaDateRange,
    recurrence: RecurrenceType,
    pickerState: LocationPickerState
) {
    // we can reach this check when the very first request is pending, in this case displayedSuggestionsTimestamp will be null since
    // we set it when the request completes, so if displayedSuggestionsTimestamp is null and other requested parameter are the same then
    // we consider that there already was a request
    const resultsAreLessThanTwoMinsAgo =
        pickerState.displayedSuggestionsTimestamp == null ||
        differenceInMinutes(now(), pickerState.displayedSuggestionsTimestamp) <= 2;

    return (
        queryString === pickerState.queryString &&
        searchMode === pickerState.requestedSuggestionsSearchMode &&
        areAttendeeEqual(requiredAttendees, pickerState.requestedSuggestionsRequiredAttendees) &&
        areAttendeeEqual(optionalAttendees, pickerState.requestedSuggestionsOptionalAttendees) &&
        isEqual(timeSlot, pickerState.requestedSuggestionsTimeSlot) &&
        isEqual(recurrence, pickerState.requestedSuggestionsRecurrence) &&
        resultsAreLessThanTwoMinsAgo &&
        smtpAddressOfAccount === pickerState.smtpAddressOfAccount
    );
}

function areAttendeeEqual(actualAttendees: string[], expectedAttendees: string[]): boolean {
    if (actualAttendees == expectedAttendees) {
        return true;
    } else if (!actualAttendees || !expectedAttendees) {
        return false;
    } else if (actualAttendees.length != expectedAttendees.length) {
        return false;
    } else {
        return !actualAttendees.some((attendee, index) => !expectedAttendees.includes(attendee));
    }
}
